import type { MutableRefObject } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Check as CheckIcon } from '@mui/icons-material'

import { HighlightList } from '@/apps/latest/components/molecules/HighlightList'
import {
  PROCESSING_STEPS,
  ProcessingChecklist,
} from '@/apps/latest/components/molecules/ProcessingChecklist'
import { ScrollingTranscript } from '@/apps/latest/components/molecules/ScrollingTranscript'
import {
  MemoPanel,
  type MemoFlush,
} from '@/apps/latest/features/yakureki/components/MemoPanel'
import { AttachmentList } from '@/apps/latest/features/attachments/AttachmentList'
import {
  GENERATING_BLOCKED_REASON,
  PHARMACIST_ATTACHMENT_KINDS,
} from '@/apps/latest/features/yakureki/attachments'
import type { RecordingViewModel } from '@/apps/latest/shared/recording-status/types'
import { ICON_SIZE } from '@/shared/styles/typography'
import type { YakurekiDetail } from '@/shared/types/yakurekiCore'
import type { Highlight } from '@/shared/types/highlight'

import type { MeetingTab } from './MeetingTabBar'

// scroll する tab (= highlight / transcript / memo) は固定高さ + 内部 scroll で
// タブ切替の visual shimmer を抑える. 高さは ScrollingTranscript と整合 (= 240px)。
// スマホ幅 (xs) では viewport を圧迫しすぎるため低めにする.
const BODY_SX = {
  height: { xs: 180, sm: 240 },
  overflow: 'auto' as const,
  p: 2,
}

// 短い prompt / status (= idle / justCompleted / checklist) は content-driven (= 無駄余白を
// 確保せず自然サイズ). 上下中央寄せ + 縦 padding のみ.
const BODY_CENTERED_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  px: 2,
  py: 3,
}

interface Props {
  recordingStatus: RecordingViewModel
  activeTab: MeetingTab
  highlights: Highlight[]
  event: YakurekiDetail | null
  // memo タブで MemoPanel に渡す yakurekiId. event.id と等価だが、 idle/event 未 fetch
  // のときも memo を編集したいケースに備えて別 prop で受ける.
  yakurekiId: string | null
  // 録音中の Chirp finals を改行で連結したライブ文字起こし.
  // status='recording'/'stopping' で transcript tab に表示する. finalize 後は dictated.sentences に切替.
  liveTranscript?: string
  // Chirp の interim (= 揮発の途中認識結果). ScrollingTranscript で italic + muted で表示.
  liveInterim?: string
  // memo タブが開いている間だけ MemoPanel の flush 関数が載る. page が「メモを確実に
  // 保存してから読む」ために使う (= メモから SOAP 生成).
  memoFlushRef?: MutableRefObject<MemoFlush | null>
}

const FailedBanner = () => (
  <Typography variant="caption" color="warning.main">
    最終整形に失敗 · preview を表示中 · 完全版は後で再生成
  </Typography>
)

const dictatedToText = (event: YakurekiDetail | null): string => {
  if (!event?.dictated?.sentences) return ''
  return event.dictated.sentences
    .map((s) => (s.speaker ? `[${s.speaker}] ${s.sentence}` : s.sentence))
    .join('\n')
}

// MeetingBlock Body のタブ:
//   highlight (ハイライト) / transcript (文字起こし) / memo / attachments (添付資料・β)
//   (暫定 SOAP tab は廃止)
//
// 表示の切替軸 (= viewStatus 駆動):
//   - idle: 録音開始 prompt
//   - recording: 経過秒 / live transcript / highlight
//   - stopping / generating: ProcessingChecklist のみ (= 生成中表示の gap regression を構造的に解消)
//   - completed: 「処理完了」 notice (= 1 回)
//   - failed: FailedBanner + preview
export const Body = ({
  recordingStatus,
  activeTab,
  highlights,
  event,
  yakurekiId,
  liveTranscript,
  liveInterim,
  memoFlushRef,
}: Props) => {
  const {
    status,
    checklistVisible,
    checklistStep,
    checklistAllDone,
    justCompleted,
  } = recordingStatus
  const failed = status === 'failed'

  // memo は録音前/生成中を問わず常に編集可能 (= 服薬指導前後の覚書). 添付資料は録音前・録音中に足せ、
  // 生成中 (音声の送信 + SOAP 生成) は足せない旨の 1 行をタブの中に出す (一覧は見える).
  // idle / 完了直後 notice / checklist の各 gate はこれらのタブでは bypass する.
  const isAlwaysEditableTab =
    activeTab === 'memo' || activeTab === 'attachments'

  // idle 分岐:
  //   - event.soaps 既存 (= 完了済 event の fresh visit): activeTab 分岐に fall through
  //   - memo タブは録音前から編集可 (= 服薬指導前の覚書) — idle gate を bypass.
  //   - その他 idle: 録音開始 prompt.
  if (status === 'idle' && !event?.soaps && !isAlwaysEditableTab) {
    return (
      <Box sx={BODY_CENTERED_SX}>
        <Typography variant="body2" color="text.secondary">
          上のマイクボタンで録音を開始
        </Typography>
      </Box>
    )
  }

  // status='completed' に切り替わった直後 1 render (= justCompleted=true) かつ
  // event.soaps が無い場合のみ 「処理完了」 notice を出す.
  // (= justCompleted は viewModel 内で 1 render flag)
  if (justCompleted && !event?.soaps && !isAlwaysEditableTab) {
    return (
      <Box sx={BODY_CENTERED_SX}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 0.5,
          }}
        >
          <CheckIcon sx={{ fontSize: ICON_SIZE.md, color: 'text.primary' }} />
          <Typography variant="body2" color="text.primary">
            処理完了
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          左の「最近の薬歴」から記録を確認ください
        </Typography>
      </Box>
    )
  }

  // checklist 表示中 (停止信号送信中 / SOAP 生成中 / 整形完了直後) は HighlightList を隠して
  // 進捗 UI に集中させる. step list は **左寄せ** (= list UI 通例 / 中央寄せは可読性低下).
  if (checklistVisible && !isAlwaysEditableTab) {
    return (
      <Box sx={BODY_SX}>
        <ProcessingChecklist
          steps={PROCESSING_STEPS}
          currentIdx={checklistStep}
          allDone={checklistAllDone}
        />
      </Box>
    )
  }

  if (activeTab === 'highlight') {
    const extracting = status === 'recording'
    // 録音中で highlights 未抽出時は ScrollingTranscript fallback (= 全 tab で文字起こし SSoT 統一).
    const showTranscriptFallback =
      extracting &&
      highlights.length === 0 &&
      (liveTranscript ?? '').trim().length > 0
    return (
      <Box sx={BODY_SX}>
        <Stack spacing={2}>
          {failed && <FailedBanner />}
          {showTranscriptFallback ? (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                ハイライト抽出中 · 暫定: 文字起こし
              </Typography>
              <ScrollingTranscript
                finals={liveTranscript ?? ''}
                interim={liveInterim}
                maxHeight={160}
              />
            </Stack>
          ) : (
            <HighlightList
              highlights={highlights}
              order="asc"
              fadeAfter={3}
              emptyMessage={
                extracting ? 'ハイライト抽出待ち...' : 'ハイライトはありません'
              }
            />
          )}
        </Stack>
      </Box>
    )
  }

  if (activeTab === 'memo') {
    // 薬剤師手書きメモ. event.memo_items を MemoPanel が freeform 編集 + auto-save.
    // 全 state で編集可能.
    if (!yakurekiId) {
      return (
        <Box sx={BODY_CENTERED_SX}>
          <Typography variant="body2" color="text.secondary">
            患者を選択するとメモを編集できます
          </Typography>
        </Box>
      )
    }
    return (
      <Box sx={BODY_SX}>
        <MemoPanel
          flushRef={memoFlushRef}
          yakurekiId={yakurekiId}
          memoItems={event?.memo_items ?? []}
        />
        {failed && (
          <Box sx={{ mt: 2 }}>
            <FailedBanner />
          </Box>
        )}
      </Box>
    )
  }

  if (activeTab === 'attachments') {
    // 添付資料: 足す口 ([撮る][選ぶ]) と 1 件 1 行の一覧. 生成は開始時点の資料を読み、途中で足した分は
    // 黙って入らないので、生成中は足させない (完了後は SOAP の下の「資料」の節から足す。ADR-0053 決定 3).
    if (!yakurekiId) {
      return (
        <Box sx={BODY_CENTERED_SX}>
          <Typography variant="body2" color="text.secondary">
            患者を選択すると資料を添付できます
          </Typography>
        </Box>
      )
    }
    return (
      <Box sx={BODY_SX}>
        <AttachmentList
          fill
          yakurekiId={yakurekiId}
          kinds={PHARMACIST_ATTACHMENT_KINDS}
          skippedIds={[]}
          blockedReason={
            status === 'stopping' || status === 'generating'
              ? GENERATING_BLOCKED_REASON
              : undefined
          }
        />
      </Box>
    )
  }

  if (activeTab === 'transcript') {
    // 優先順位: 録音中は Chirp live finals → 完了後は dictated.sentences (Pass2 話者分離)
    const isLive =
      status === 'recording' || status === 'paused' || status === 'stopping'
    const dictatedText = dictatedToText(event)
    const finals = isLive
      ? liveTranscript || ''
      : dictatedText || liveTranscript || ''
    const interim = isLive ? liveInterim : undefined
    const emptyMessage =
      status === 'recording'
        ? '会話の蓄積を待っています...'
        : '文字起こし待ち...'
    return (
      <Box sx={{ ...BODY_SX, display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          {failed && <FailedBanner />}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ScrollingTranscript
              finals={finals}
              interim={interim}
              emptyMessage={emptyMessage}
            />
          </Box>
        </Stack>
      </Box>
    )
  }

  return null
}
